import React, { memo, useCallback, useEffect, useState } from 'react';
import { Loader } from '@app/components';
import { Button } from 'components/ui/button';
import { IconPlusCircle24 } from '@app/icons';
import { ModalReview } from '@app/modals';
import Comment from 'pages/Profile/components/Comment';
import { Review, UserType } from '@app/models';
import { getReviews } from 'api/reviews';
import { PageableParams } from '@app/api';
import { fullName } from '@app/helpers';
import { format } from 'date-fns';
import { useUserContext } from '@app/providers';

interface Props {
  userId?: string;
  companyId?: string;
  vehicleId?: string;
  showButton: boolean;
  userType?: UserType;
}

function Reviews(props: Props) {
  const { userId, vehicleId, companyId, showButton, userType } = props;
  const { user, company } = useUserContext();
  const [pending, setPending] = useState<boolean>(true);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isReviewed, setIsReviewed] = useState<boolean>(false);

  // Don't show "Add Review" button for individual users (specialists)
  const shouldShowButton = showButton && userType !== UserType.INDIVIDUAL;

  const onShowModal = useCallback(() => {
    setModalVisible(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const getData = useCallback(async () => {
    try {
      const params: PageableParams & Partial<Review> = {
        page: 1,
        perPage: 20,
      };

      if (!!companyId) {
        params.companyId = companyId;
      }

      if (!!userId) {
        params.userId = userId;
      }

      if (!!vehicleId) {
        params.vehicleId = vehicleId;
      }

      const response = await getReviews(params);
      const findReview = response.data.find(
        (elem) =>
          user.id === elem.authorId ||
          (company && company.id === elem.authorCompanyId)
      );

      if (findReview) setIsReviewed(true);

      setReviews(response.data);
      setPending(false);
    } catch (e) {
      setPending(false);
    }
  }, [company, companyId, user.id, userId, vehicleId]);

  useEffect(() => {
    getData();
  }, [getData]);

  if (pending) {
    return (
      <div className="text-center pt-6 box-border">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      {shouldShowButton && !isReviewed && (
        <div className="flex justify-end mb-4">
          <Button onClick={onShowModal}>
            <IconPlusCircle24 />
            Добавить отзыв
          </Button>
          <ModalReview
            visible={modalVisible}
            onClose={onCloseModal}
            companyId={companyId}
            userId={userId}
            vehicleId={vehicleId}
            getData={getData}
          />
        </div>
      )}
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground text-lg">Нет отзывов</p>
        </div>
      ) : (
        reviews.map((review) => (
          <Comment
            key={review.id}
            imgSrc=""
            rating={review.rating}
            likes={false}
            comments={[{ value: review.description }]}
            author={review.author ? fullName(review.author) : 'Аноним'}
            date={format(new Date(review.createdAt), 'dd.MM.yyyy')}
            theme={review.contract?.name || null}
            organization={review.authorCompany?.name || null}
          />
        ))
      )}
    </div>
  );
}

export default memo(Reviews);
